/**
 * Pure parsing/filtering for the R4-C research projection.
 *
 * Keeping this module free of React and network calls lets the behaviour be tested
 * directly: the UI must not "fix" a summary by guessing, and it must not turn an
 * untrusted URL into a link.
 */

import type {
  ResearchEvaluation,
  ResearchResult,
  ResearchResultAvailable,
  ResearchSummary,
} from "../api/client";

export type ResearchFilter = "all" | "accept" | "needs_review" | "reject";

const PROJECTION_SCHEMA_VERSION = "r4.research_result.v1";
const CLASSIFICATIONS = new Set(["accept", "needs_review", "reject"]);
const ORGANIZATION_TEXT_FIELDS = [
  "name",
  "type",
  "country",
  "city",
  "website",
  "language",
  "member_estimate_band",
  "facility_notes",
] as const;

type UnknownRecord = Record<string, unknown>;

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isText(value: unknown): value is string {
  return typeof value === "string";
}

function isTextOrNull(value: unknown): value is string | null {
  return value === null || typeof value === "string";
}

function isNonNegativeInteger(value: unknown): value is number {
  return typeof value === "number" && Number.isInteger(value) && value >= 0;
}

function isTextArray(value: unknown, maximum: number): value is string[] {
  return (
    Array.isArray(value) &&
    value.length <= maximum &&
    value.every((entry) => isText(entry))
  );
}

function isStringRecord(value: unknown): value is Record<string, string> {
  return isRecord(value) && Object.values(value).every((entry) => isText(entry));
}

function isSource(value: unknown): boolean {
  return (
    isRecord(value) &&
    isText(value.url) &&
    isText(value.observed_at) &&
    isText(value.claim)
  );
}

function isOrganization(value: unknown): boolean {
  if (!isRecord(value)) return false;
  if (!ORGANIZATION_TEXT_FIELDS.every((field) => isTextOrNull(value[field]))) {
    return false;
  }
  return isStringRecord(value.field_provenance);
}

function isEvaluation(value: unknown): boolean {
  if (!isRecord(value)) return false;
  if (!isText(value.organization_id) || !CLASSIFICATIONS.has(value.classification as string)) {
    return false;
  }
  if (!isTextArray(value.reason_codes, 20) || value.reason_codes.length === 0) {
    return false;
  }
  if (!isTextOrNull(value.duplicate_of)) return false;
  if (!isNonNegativeInteger(value.fit_score) || !isNonNegativeInteger(value.confidence)) {
    return false;
  }
  if (!isOrganization(value.organization)) return false;
  if (!isTextArray(value.missing_fields, 32)) return false;
  return (
    Array.isArray(value.sources) &&
    value.sources.length <= 20 &&
    value.sources.every((source) => isSource(source))
  );
}

function isContractRejection(value: unknown): boolean {
  return (
    isRecord(value) &&
    isText(value.organization_id) &&
    isText(value.code) &&
    isText(value.detail)
  );
}

function isSummary(value: unknown): value is ResearchSummary {
  return (
    isRecord(value) &&
    isNonNegativeInteger(value.produced) &&
    isNonNegativeInteger(value.accepted) &&
    isNonNegativeInteger(value.needs_review) &&
    isNonNegativeInteger(value.rejected)
  );
}

function isCost(value: unknown): boolean {
  if (!isRecord(value) || typeof value.known !== "boolean") return false;
  if (value.known === false) return true;
  return (
    isNonNegativeInteger(value.actual_micros) &&
    isNonNegativeInteger(value.estimated_micros) &&
    isNonNegativeInteger(value.unavailable_count) &&
    (value.currency === undefined || isText(value.currency))
  );
}

/**
 * Runtime schema gate for data arriving from the job-detail API.
 *
 * The TypeScript types are erased at runtime, so a proxy, an older backend or a
 * malformed projection can otherwise reach `summaryMatches`/`map`. Unknown versions,
 * unknown states and missing/malformed nested fields all return `null`; the caller
 * renders a bounded unavailable state and never partial rows, sources or cost.
 */
export function parseResearchResult(value: unknown): ResearchResult | null {
  if (!isRecord(value) || value.schema_version !== PROJECTION_SCHEMA_VERSION) {
    return null;
  }

  if (value.state === "unavailable") {
    return (
      isText(value.reason) &&
      value.reason.length > 0 &&
      value.reason.length <= 64
    )
      ? (value as unknown as ResearchResult)
      : null;
  }
  if (value.state !== "available") return null;

  if (!isRecord(value.workflow)) return null;
  if (
    !isText(value.workflow.key) ||
    !isNonNegativeInteger(value.workflow.version) ||
    !isText(value.workflow.content_hash)
  ) {
    return null;
  }
  if (!isText(value.evaluated_on) || !isText(value.snapshot_hash)) return null;
  if (!isText(value.source_hash) || !isText(value.review_output_hash)) return null;

  if (!isRecord(value.fixture)) return null;
  if (
    !isText(value.fixture.id) ||
    !isText(value.fixture.version) ||
    !isText(value.fixture.hash)
  ) {
    return null;
  }

  if (!isSummary(value.summary)) return null;
  if (
    !Array.isArray(value.evaluations) ||
    value.evaluations.length > 10 ||
    !value.evaluations.every((evaluation) => isEvaluation(evaluation))
  ) {
    return null;
  }
  if (
    !Array.isArray(value.contract_rejections) ||
    value.contract_rejections.length > 10 ||
    !value.contract_rejections.every((rejection) => isContractRejection(rejection))
  ) {
    return null;
  }
  if (value.cost !== undefined && !isCost(value.cost)) return null;

  return value as unknown as ResearchResult;
}

export type ResearchOutcome =
  | {
      kind: "evaluation";
      organizationId: string;
      classification: "accept" | "needs_review" | "reject";
      evaluation: ResearchEvaluation;
    }
  | {
      kind: "contract_rejection";
      organizationId: string;
      classification: "reject";
      rejection: {
        organization_id: string;
        code: string;
        detail: string;
      };
    };

export function classifyOutcomes(result: ResearchResultAvailable): ResearchOutcome[] {
  return [
    ...result.evaluations.map(
      (evaluation): ResearchOutcome => ({
        kind: "evaluation",
        organizationId: evaluation.organization_id,
        classification: evaluation.classification,
        evaluation,
      }),
    ),
    ...result.contract_rejections.map(
      (rejection): ResearchOutcome => ({
        kind: "contract_rejection",
        organizationId: rejection.organization_id,
        classification: "reject",
        rejection,
      }),
    ),
  ];
}

export function computedSummary(result: ResearchResultAvailable): ResearchSummary {
  const outcomes = classifyOutcomes(result);
  const counts = { accept: 0, needs_review: 0, reject: 0 };
  for (const outcome of outcomes) counts[outcome.classification] += 1;
  return {
    produced: outcomes.length,
    accepted: counts.accept,
    needs_review: counts.needs_review,
    rejected: counts.reject,
  };
}

/** Exact equality, including produced arithmetic. No coercion and no defaults. */
export function summaryMatches(result: ResearchResultAvailable): boolean {
  const computed = computedSummary(result);
  const actual = result.summary;
  return (
    actual.produced === computed.produced &&
    actual.accepted === computed.accepted &&
    actual.needs_review === computed.needs_review &&
    actual.rejected === computed.rejected &&
    actual.produced === actual.accepted + actual.needs_review + actual.rejected
  );
}

export function filterOutcomes(
  outcomes: ResearchOutcome[],
  filter: ResearchFilter,
): ResearchOutcome[] {
  if (filter === "all") return outcomes;
  return outcomes.filter((outcome) => outcome.classification === filter);
}

/** Absolute HTTP(S) links only. `javascript:`, `data:` and user-info are refused. */
export function safeHttpUrl(value: string | null | undefined): string | null {
  if (!value) return null;
  try {
    const parsed = new URL(value);
    if (parsed.protocol !== "http:" && parsed.protocol !== "https:") return null;
    if (!parsed.hostname) return null;
    if (parsed.username || parsed.password) return null;
    return parsed.toString();
  } catch {
    return null;
  }
}

const REASON_LABELS: Record<string, string> = {
  evidence_accepted: "dokazi prihvaćeni",
  uncited_claim: "necitirani navod",
  unsourced_verified_field: "verificirano polje bez izvora",
  contradictory_sources: "proturječni izvori",
  stale_source: "zastarjeli izvor",
  injection_attempt: "pokušaj ubacivanja",
  suppressed: "na suppression listi",
  duplicate: "duplikat",
  missing_evidence: "nedostaju dokazi",
  contract_rejected: "ugovorno odbijeno",
};

export function reasonLabel(code: string): string {
  return REASON_LABELS[code] ?? code;
}

export function classificationLabel(
  classification: ResearchOutcome["classification"],
): string {
  if (classification === "accept") return "prihvaćeno";
  if (classification === "needs_review") return "za pregled";
  return "odbijeno";
}

export function unavailableReasonLabel(reason: string): string {
  switch (reason) {
    case "job_not_finished":
      return "Posao još nije završio; rezultat se ne prikazuje unaprijed.";
    case "unsupported_workflow":
      return "Ovaj posao nije R4 istraživački workflow s rezultatom.";
    case "workflow_mismatch":
      return "Workflow posla ne odgovara pregledanom R4 workflowu.";
    case "step_linkage_mismatch":
      return "Koraci posla ne odgovaraju očekivanom R4 lancu.";
    case "projection_too_large":
      return "Rezultat prelazi dopuštenu veličinu prikaza.";
    case "invalid_projection":
      return "Projekcija rezultata nema poznati oblik; prikaz je blokiran.";
    case "integrity_rejected":
    case "projection_unavailable":
      return "Rezultat nije moguće provjeriti; prikaz je blokiran.";
    default:
      return "Rezultat nije dostupan.";
  }
}
